import type { AdminFaq, LocalizedPair } from '@diora/contracts';
import { ChevronDown, ChevronUp, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { BilingualField } from '@/components/bilingual';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { PublishBlockers, RowActions, StatusBadge, TranslationBadge } from '@/components/records';
import { Button, DataTable, EmptyState, Pagination, SkeletonRows, Toolbar } from '@/components/ui';
import {
  apiPatch,
  apiPost,
  useAdminList,
  useAdminMutation,
  useLifecycle,
  useListParams,
  useReorder,
} from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { ListFilters } from './_filters';

const URL = '/admin/faqs';
/** The list this screen's form pages return to. */
const LIST = '/content/faqs';
const INVALIDATE = [['faqs'], ['faq'], qk.dashboard];

export function FaqsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const [params, setParams] = useListParams();
  const list = useAdminList<AdminFaq>(qk.faqs(params), URL, params);
  const lifecycle = useLifecycle(URL, INVALIDATE);
  const reorder = useReorder(URL, INVALIDATE);

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const onLastPage = page * pageSize >= list.total;

  function reorderBy(id: string, delta: number) {
    if (!list.contentRevision) return;
    reorder.mutate({
      id,
      delta,
      visible: list.rows.map((row) => row.id),
      contentRevision: list.contentRevision,
    });
  }

  return (
    <>
      <PageHeader
        width="narrow"
        title={t('faqs.title')}
        description={t('faqs.subtitle')}
        actions={
          canEdit && (
            <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
              <Plus className="dio-icon" aria-hidden="true" />
              {t('faqs.newTitle')}
            </Link>
          )
        }
      />

      <Toolbar>
        <ListFilters params={params} onChange={setParams} />
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<FileText aria-hidden="true" />} title={t('faqs.empty')} hint={t('faqs.halfAnswer')} />
      ) : (
        <DataTable
          caption={t('faqs.title')}
          columns={[
            t('faqs.question'),
            t('common.status'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row, index) => (
            <tr key={row.id}>
              <td>
                {/* A question is a sentence, so unlike a dish name the two
                    languages stack rather than sitting side by side. */}
                <p lang="ar" dir="rtl" className="dio-table__name">
                  {row.question.ar || '—'}
                </p>
                <p lang="en" dir="ltr" className="dio-table__sub">
                  {row.question.en || '—'}
                </p>
              </td>

              <td>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={row.publicationStatus} />
                  <TranslationBadge translation={row.translation} />
                </div>
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  {canEdit && (
                    <>
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveUp')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          !list.contentRevision ||
                          (index === 0 && page === 1)
                        }
                        onClick={() => reorderBy(row.id, -1)}
                      >
                        <ChevronUp className="dio-icon" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        iconOnly
                        variant="ghost"
                        aria-label={t('common.moveDown')}
                        disabled={
                          row.publicationStatus === 'ARCHIVED' ||
                          reorder.isPending ||
                          !list.contentRevision ||
                          (index === list.rows.length - 1 && onLastPage)
                        }
                        onClick={() => reorderBy(row.id, 1)}
                      >
                        <ChevronDown className="dio-icon" aria-hidden="true" />
                      </Button>
                    </>
                  )}
                  <Link to={`${LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {canEdit ? t('common.edit') : t('common.details')}
                  </Link>
                  <RowActions
                    status={row.publicationStatus}
                    translation={row.translation}
                    canEdit={canEdit}
                    busy={lifecycle.busy}
                    label={t('common.openMenuFor', {
                      name: row.question.ar || row.question.en,
                    })}
                    handlers={{
                      onPublish: () => lifecycle.publish.mutate({ id: row.id, version: row.version }),
                      onUnpublish: () => lifecycle.unpublish.mutate({ id: row.id, version: row.version }),
                      onArchive: () => lifecycle.archive.mutate({ id: row.id, version: row.version }),
                      onRestore: () => lifecycle.restore.mutate({ id: row.id, version: row.version }),
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={list.total}
        onPage={(next) => setParams({ page: next })}
      />

    </>
  );
}

export function FaqFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const record = useRecord<AdminFaq>(qk.faq(id ?? ''), URL, id);

  if (!id) return <FaqForm faq={null} canEdit={canEdit} />;

  return (
    <RecordPage title={t('faqs.editTitle')} backTo={LIST} record={record}>
      {(row) => <FaqForm key={row.id} faq={row} canEdit={canEdit} />}
    </RecordPage>
  );
}

function FaqForm({ faq, canEdit }: { faq: AdminFaq | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [question, setQuestion] = useState<LocalizedPair>(faq?.question ?? { ar: '', en: '' });
  const [answer, setAnswer] = useState<LocalizedPair>(faq?.answer ?? { ar: '', en: '' });

  const dirty = useDirty({ question, answer });

  const save = useAdminMutation(
    () => {
      const body = {
        questionAr: question.ar,
        questionEn: question.en,
        answerAr: answer.ar,
        answerEn: answer.en,
      };
      return faq
        ? apiPatch(`${URL}/${faq.id}`, { ...body, expectedVersion: faq.version })
        : apiPost(URL, body);
    },
    {
      successMessage: faq ? t('common.saved') : t('common.created'),
      invalidate: INVALIDATE,
      onSuccess: () => navigate(LIST),
    },
  );

  return (
    <FormPage
      title={faq ? t('faqs.editTitle') : t('faqs.newTitle')}
      description={t('faqs.subtitle')}
      backTo={LIST}
      dirty={dirty}
      saving={save.isPending}
      canSave={question.ar.trim().length > 0}
      onSave={canEdit ? () => save.mutate(undefined) : undefined}
    >
      {faq && <PublishBlockers translation={faq.translation} />}
      <BilingualField
        label={t('faqs.question')}
        value={question}
        onChange={setQuestion}
        required
        maxLength={200}
        disabled={!canEdit}
      />
      <BilingualField
        label={t('faqs.answer')}
        hint={t('faqs.halfAnswer')}
        value={answer}
        onChange={setAnswer}
        required
        multiline
        rows={6}
        maxLength={2000}
        disabled={!canEdit}
      />
    </FormPage>
  );
}
